import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Package, Receipt, Wallet, UserCircle, LogOut, Search, Sun, Moon, UserCog,
  FileText, Inbox, Image, ClipboardList, Factory, Truck, Menu, X, Send, MessageCircle,
  ShoppingBag, Tag, Gift, Boxes, Layers, Megaphone, Bell, Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

// Menu shown for staff / admin (never for customers)
const STAFF_MENU = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: null },
  { to: "/customers", label: "Customers", icon: Users,
    roles: ["admin", "office_staff", "sales_executive", "accountant"] },
  { to: "/orders", label: "Orders", icon: Package,
    roles: ["admin", "office_staff", "sales_executive", "designer",
      "production_manager", "machine_operator", "delivery_staff", "accountant"] },
  { to: "/quotations", label: "Quotations", icon: ClipboardList,
    roles: ["admin", "office_staff", "sales_executive", "accountant"] },
  { to: "/production", label: "Production", icon: Factory,
    roles: ["admin", "production_manager", "machine_operator", "office_staff"] },
  { to: "/dispatch", label: "Dispatch", icon: Truck,
    roles: ["admin", "delivery_staff", "office_staff", "production_manager"] },
  { to: "/invoices", label: "Invoices", icon: Receipt,
    roles: ["admin", "office_staff", "accountant", "sales_executive"] },
  { to: "/payments", label: "Payments", icon: Wallet,
    roles: ["admin", "office_staff", "accountant"] },
  { to: "/catalog", label: "Catalog", icon: Boxes, roles: ["admin"] },
  { to: "/hero-slides", label: "Home Hero", icon: Layers, roles: ["admin"] },
  { to: "/offers", label: "Offer Strip", icon: Megaphone, roles: ["admin"] },
  { to: "/offer-popup", label: "Offer Popup", icon: Sparkles, roles: ["admin"] },
  { to: "/coupons", label: "Coupons", icon: Tag, roles: ["admin"] },
  { to: "/giftcards", label: "Gift Cards", icon: Gift, roles: ["admin"] },
  { to: "/staff", label: "Staff", icon: UserCog, roles: ["admin"] },
  { to: "/notifications", label: "Notifications", icon: Bell,
    roles: ["admin", "office_staff", "sales_executive", "accountant", "production_manager", "delivery_staff", "designer", "machine_operator"] },
  { to: "/reports", label: "Reports", icon: FileText, roles: ["admin", "accountant"] },
  { to: "/enquiries", label: "Enquiries", icon: Inbox,
    roles: ["admin", "sales_executive", "office_staff"] },
  { to: "/media", label: "Website Photos", icon: Image, roles: ["admin"] },
  { to: "/testimonials", label: "Testimonials", icon: MessageCircle, roles: ["admin"] },
  { to: "/vacancies", label: "Career Vacancies", icon: Send, roles: ["admin"] },
  { to: "/shipping", label: "Shipping", icon: Truck, roles: ["admin"] },
];

// Menu shown ONLY for customer role
const CUSTOMER_MENU = [
  { to: "/portal", label: "My Portal", icon: UserCircle },
  { to: "/my/orders", label: "Orders", icon: Package },
  { to: "/my/quotations", label: "Quotations", icon: ClipboardList },
  { to: "/my/payments", label: "Payments", icon: Wallet },
  { to: "/my/enquiries", label: "Enquiry", icon: Send },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (dark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [dark]);

  const isCustomer = user?.role === "customer";
  const menu = isCustomer
    ? CUSTOMER_MENU
    : STAFF_MENU.filter((m) => !m.roles || m.roles.includes(user?.role) || user?.role === "super_admin");

  const handleSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/orders?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" />
      )}

      {/* Sidebar */}
      <aside
        style={{ backgroundColor: "#000000", color: "#f4f4f5" }}
        className={`fixed lg:static z-50 top-0 left-0 h-full w-64 flex-shrink-0 flex flex-col transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-800 bg-black flex items-center justify-between">
          <Link to={isCustomer ? "/portal" : "/dashboard"} className="flex items-center" data-testid="brand-link">
            <Logo height={44} />
          </Link>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-zinc-400 hover:text-white p-1" data-testid="sidebar-close-btn">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-0.5 overflow-y-auto">
          {menu.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.to === "/portal"}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-[#FF6B00] text-white" : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <m.icon size={16} />
              <span>{m.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-4">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Signed in as</div>
          <div className="text-sm font-semibold truncate text-white" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 mb-3 capitalize">{user?.role?.replace(/_/g, " ")}</div>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            style={{ color: "#ffffff", borderColor: "#3f3f46", background: "transparent" }}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold border-2 hover:bg-white hover:text-black transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={14} /> Logout
          </button>
          <button
            onClick={() => navigate("/change-password")}
            className="mt-2 w-full text-xs text-zinc-400 hover:text-white text-center py-1"
            data-testid="change-password-nav"
          >
            Change password
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-[hsl(var(--background))]/80 backdrop-blur border-b border-[hsl(var(--border))] flex items-center px-4 lg:px-6 gap-2 lg:gap-4">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 border border-[hsl(var(--border))]" data-testid="sidebar-open-btn">
            <Menu size={16} />
          </button>
          {!isCustomer && (
            <form onSubmit={handleSearch} className="flex-1 max-w-xl">
              <div className="flex items-center border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3">
                <Search size={16} className="text-muted-foreground" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search orders, customers, invoices…"
                  data-testid="global-search-input"
                  className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
                />
                <kbd className="text-[10px] text-muted-foreground font-mono hidden sm:inline">↵</kbd>
              </div>
            </form>
          )}
          {isCustomer && <div className="flex-1 font-display font-black text-lg tracking-tight">Customer Portal</div>}
          {!isCustomer && <NotificationBell />}
          <button
            onClick={() => setDark(!dark)}
            data-testid="theme-toggle"
            className="p-2 border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
            title="Toggle theme"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden" data-testid="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
